import React from 'react';
import { format, parseISO } from 'date-fns';
import { motion } from 'framer-motion';
import GlowScoreBadge from '../common/GlowScoreBadge';
import TagPill from '../common/TagPill';
import SafeIcon from '../../common/SafeIcon';
import { FiDroplet, FiSun, FiMoon, FiActivity } from 'react-icons/fi';

export default function TimelineCard({ entry, onClick }) {
  const amDone = entry.routines?.AM?.length > 0;
  const pmDone = entry.routines?.PM?.length > 0;
  const suppCount = entry.supplements?.length || 0;
  const treatCount = entry.treatments?.length || 0;

  const primaryPhoto = entry.photos?.find(p => p.useInTimelapse)?.url
    || entry.photos?.[0]?.url
    || entry.imageUrl;
  const photoCount = entry.photos?.length || (entry.imageUrl ? 1 : 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.985 }}
      onClick={() => onClick(entry)}
      className="glass-card rounded-[2rem] overflow-hidden cursor-pointer"
    >
      {/* Photo */}
      <div className="relative h-56 w-full overflow-hidden bg-white/40">
        {primaryPhoto ? (
          <>
            <img src={primaryPhoto} alt="skin" className="w-full h-full object-cover object-top" />
            {photoCount > 1 && (
              <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm px-2.5 py-1 rounded-full">
                <span className="text-white font-sans text-xs font-medium">+{photoCount - 1} more</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-300">
            <span className="text-5xl mb-2">✦</span>
            <span className="text-sm font-sans">No photo</span>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
        
        <div className="absolute top-4 right-4">
          <GlowScoreBadge score={entry.glowScore} />
        </div>
        
        <div className="absolute bottom-4 left-5">
          <p className="text-white font-sans text-2xl font-medium tracking-tight leading-none drop-shadow-md">
            {format(parseISO(entry.date), 'MMM d')}
          </p>
          <p className="text-white/90 font-sans text-xs mt-1 drop-shadow-md">
            {format(parseISO(entry.date), 'EEEE')}
          </p>
        </div>

        {treatCount > 0 && (
          <div className="absolute top-4 left-4 bg-white/80 backdrop-blur-md text-slate-800 text-xs px-3 py-1.5 rounded-full font-sans font-semibold">
            💉 {entry.treatments[0].type}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {entry.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {entry.tags.map(tag => <TagPill key={tag} tag={tag} small />)}
          </div>
        )}
        
        <div className="flex items-center gap-4 text-slate-400">
          <div className={`flex items-center gap-1.5 ${entry.water >= 64 ? 'text-teal-600' : 'text-slate-400'}`}>
            <SafeIcon icon={FiDroplet} className="text-sm" />
            <span className="text-xs font-sans font-medium">{entry.water}oz</span>
          </div>
          <div className="flex items-center gap-1.5">
            <SafeIcon icon={FiSun} className={`text-sm ${amDone ? 'text-slate-800' : 'text-slate-300'}`} />
            <SafeIcon icon={FiMoon} className={`text-sm ${pmDone ? 'text-slate-800' : 'text-slate-300'}`} />
          </div>
          {suppCount > 0 && (
            <div className="flex items-center gap-1.5 text-purple-600">
              <SafeIcon icon={FiActivity} className="text-sm" />
              <span className="text-xs font-sans font-medium">{suppCount} supps</span>
            </div>
          )}
        </div>

        {entry.notes && (
          <p className="mt-4 text-sm font-sans text-slate-600 line-clamp-2 bg-white/40 border border-white/60 px-4 py-3 rounded-2xl">
            "{entry.notes}"
          </p>
        )}
      </div>
    </motion.div>
  );
}